<template>
    <div class="container">
        <h1>Comment déléguer ses ADAs avec Daedalus</h1>
        <p class="intro">
            Vous pouvez déleguer vos ADAs au fonctionnement de la blockchain Cardano, contre des récompenses (entre 4% et 8% d'intérêt environ). Tutoriel
            avec le portefeuille "hot" Daedalus.
        </p>

        <p>
            <a href="https://fr.wikipedia.org/wiki/HODL">HODL</a> ses ADAs, et attendre que le temps joue pour nous, c'est une bonne idée.
            Mais en voici une encore meilleure: <b>participez à la blockchain Cardano</b> et <b>gagnez des intérêts sur vos ADAs</b>.
            Cette procédure utilisera le portefeuille "hot" Daedalus, qu'on essayera de "refroidir" au passage pour plus de tranquillité d'esprit.
        </p>

        <h2>Daedalus: noeud de la blockchain Cardano</h2>

        <p>
            Daedalus est un portefeuille dédié à la <a href="/doc-cardano/qu-est-ce-que-cardano">blockchain Cardano</a>. A l'inverse de ses concurrents (comme
            <a href="https://yoroi-wallet.com/" title="Le portefeuille Yoroi">Yoroi</a> ou
            <a href="https://adalite.io/" title="Le portefeuille Adalite">Adalite</a>), Daedalus est un portefeuille "full node", un vrai noeud de la
            blockchain. Il synchronise l'ensemble de la blockchain Cardano en local, et ne fait confiance à aucune autre entité que votre machine pour
            fonctionner. En contrepartie, il demande plus d'espace disque (entre 5Go et 10Go).
        </p>

        <p>
            Commencez par <a href="https://daedaluswallet.io/en/download/">Télécharger et installer Daedalus Wallet</a>.
            Il fonctionne sur Windows, Mac et Linux. Si vous désirez "refroidir" un peu le portefeuille au passage, installez le sur un support externe
            (une clé USB ou un disque dur externe, idéalement de 16Go ou plus). Si vous possedez un
            <a href="https://www.techradar.com/news/best-secure-drives">secure drive</a> (disque dur avec code de déverrouillage), c'est le moment de
            vous en servir.
        </p>

        <h2>Securiser sa machine</h2>

        <p>
            Comme pour tout les hots wallets (même installés sur support externe), on préfèrera sécuriser la machine sur laquelle on s'en sert.
            Mais sécuriser sa machine se fait souvent, malheureusement, au détriment de la praticité et du confort d'usage. A vous de trouver le curseur qui
            vous convient, voici quelques pistes.
        </p>

        <h3>Sous Linux</h3>
        <p>
            Si vous êtes sous Linux, je pars du principe que vous savez googler et que vous êtes capables de sécuriser votre machine. Les permissions
            utilisateurs, iptables, les protections noyau, l'encrpytion disque sont vos amis.
        </p>

        <h3>Sous Windows</h3>
        <p>
            Si vous êtes sous Windows, il est recommandé d'avoir un <b>antivirus</b>, un <b>firewall</b>. Les gratuits font aussi bien l'affaire que
            les payants. Ma préférence personnelle va à <a href="https://www.avira.com/">Avira Free</a> et à
            <a href="https://personalfirewall.comodo.com/">Commodo Firewall</a>, qui, à l'inverse de certains de ses concurrents, à le mérite de ne pas
            ralentir la machine (enfin, ça c'est mon expérience).
        </p>
        <p>
            Pensez aussi à protéger l'accès physique à votre machine (mise en veille rapide, mot de passe à l'entrée de la session, etc).
            Si vous le désirez, vous pouvez créer un utilisateur spécialement pour ça.
            Certains vont même jusqu'à utiliser une machine virtuelle pour gérer leur hot wallets. Comme dit plus haut, c'est à vous de placer le curseur
            entre confort et sécurité.
        </p>

        <h3>Sous Mac</h3>
        <p>
            Les utilisateurs d'Apple ont tendance à penser (à tort) que leur matériel est sécurisé par défaut. C'est faux.
            Avec 30% de part de marché, le Mac n'est plus une exception dans le paysage de l'ordinateur personnel, et les malwares se développent
            également pour cette plateforme.
        </p>

        <p>
            Néanmoins, l'écosystème Apple ne manque pas de ressources.
            Je recommande <a href="https://www.macplanete.com/dossiers/21533/proteger-son-mac-virus-malwares">Ce tutoriel de macplanete</a> qui couvre les
            principaux aspets.
        </p>

        <h2>Créer un portefeuille sous Daedalus</h2>

        <p>
            Une fois Daedalus installé et fonctionnel, nous allons créer un portefeuille dessus, afin que vous puissez transférer vos ADAs.
            Cliquez sur le bouton "Portefeuille" en haut à gauche. Dans le menu qui s'ouvre, vous avez l'option de créer un nouveau portefeuille ou d'en
            restaurer un (depuis Daedalus ou d'autres sources) à partir de la <b>passphrase</b>.
        </p>

        <p>
            Vos addresses ne sont pas une information confidentielle à garder absolument secrète. Vous pourrez y regarder toutes les transactions
            sur <a href="https://explorer.cardano.org/en">l'explorateur Cardano</a>, vous n'aurez pas besoin de garder votre Daedalus à portée de main
            pour ça.
        </p>

        <h3>Sécuriser sa passphrase</h3>
        <p>
            Suivez la procédure pour créer votre nouveau wallet (portefeuille). Utilisez un mot de passe que vous n'utilisez nulle part ailleurs, mais
            que vous êtes certains de ne pas oublier: il vous servira à déclencher des transferts de fonds. Notez la passphrase et stocker là quelque part
            de sécurisé. C'est votre seule garantie pour restaurer votre portefeuille en cas de problème de disque dur, de perte de mot de passe, etc.
            Un coffre fort à la banque serait l'idéal, par exemple.
        </p>

        <p>
            Si vous perdez la passphrase, vous pouvez recréer une autre adresse et y transférer vos fonds avec Daedalus et votre mot de passe.
            Si vous perdez l'accès à Daedalus (disque dur corrompu, perte de la machine, oubli du mot de passe, etc), vous pouvez restaurer
            votre portefeuille à l'aide de la passphrase. Veuillez à vous prémunir des risques suivants:
        </p>
        <ul>
            <li>Accès non-autorisé à votre Daedalus (ex: piratage de votre machine avec installation de keylogger)</li>
            <li>Accès non-autorisé à passphrase (ex: piratage de votre compte dropbox sur lequel vous avez mis votre passphrase en clair)</li>
            <li>Destruction des deux (ex: incendie qui détruit votre bureau dans lequel vous avez stocké votre passphrase et votre machine)</li>
        </ul>


        <h3>Transférer ses fonds</h3>

        <p>Une fois le portefeuille créé, vous pouvez voir vos adresses Daedalus dans l'onglet <b>Receive</b> de votre nouveau portefeuille.</p>
        <img v-lazy-load data-src="/img/tuto-daedalus/adresses-daedalus.webp" alt="Adresses d'un portefeulle dans Daedalus" />

        <p>
            Nous allons pouvoir y envoyer des fonds. Si vous n'êtes pas tranquille à cette idée, faites le en 2 fois: un transfert
            de "test" de quelques ADAs, puis le reste de vos fonds. Les transferts coûtent peu (moins d'un demi-ADA en général, quelques centimes à ce
            stade), et sont très rapides (dans les quelques minutes). <b>Notez que vous verrez apparaître vos fonds sur Daedalus qu'il aura terminé de
                synchroniser la blockchain sur votre machine</b>. Pour lui, votre transaction n'existe que lorsqu'il aura récupéré le bloc correspondant de
            la blockchain. Il y a plusieurs Go à télécharger, alors pas de panique si ça prend un peu de temps. Si vous voulez vérifier votre
            transaction plus vite, pensez à utiliser <a href="https://explorer.cardano.org/en">l'explorateur Cardano</a> sur votre addresse.
        </p>

        <p>
            Envoyez vos fonds depuis votre portefeuille existant ou la plateforme d'exchange sur laquelle vous possedez vos ADAs, vers l'une des adresses
            de votre portefeuille. Ici, un exemple avec Binance: depuis le portefeuille spot, cherchez vos ADAs et cliquez sur withdraw, puis remplissez le formulaire:
        </p>
        <img v-lazy-load data-src="/img/tuto-daedalus/envoyer-ada-depuis-binance.webp" alt="Envoyer des ADAs depuis Binance" />

        <p>
            Attendez-vous à ce que le transfert prenne une dizaine de minutes ou plus, selon le portefeuille ou la plateforme que vous utilisez.
            La plupart des plateformes vous enverront un mail de confirmation. Après un moment, vous verrez vos ADAs arriver sur votre portefeuille Daedalus.
        </p>

        <p> Si votre Daedalus n'est pas encore synchronisé à la blockchain, vous pouvez regarder sur <a href="https://explorer.cardano.org/en">l'explorateur Cardano</a> si votre transfer a eu lieu </p>
        <img v-lazy-load data-src="/img/tuto-daedalus/cardano-blockchain-explorer.webp" alt="Explorateur Cardano" />

        <p>
            Depuis Daedalus, vous pouvez bien evidemment envoyer des ADAs très facilement ou vous le souhaitez. Pour ce tutoriel,
            je vais envoyer 100 ADAs sur un nouveau portefeuille (Notez les 0.17 ADAs de frais de transaction).
        </p>

        <img v-lazy-load data-src="/img/tuto-daedalus/envoyer-depuis-Daedalus.webp" alt="Envoyer des ADAs depuis Daedalus" />

        <h2>Choisir une Stake Pool et déléguer ses ADAs</h2>
        <p>
            Dans le second onglet de la barre de gauche, vous pouvez choisir <a href="/doc-cardano/qu-est-ce-qu-une-stake-pool">une stake pool</a> et lui
            déleguer vos ADAs.
        </p>

        <p>Dans le panneau de gauche, vous verrez votre portefeuille avec le badge <b>Undelegated</b>.

            <img v-lazy-load data-src="/img/tuto-daedalus/portefeuille-non-delegue.webp" alt="Portefeuille Daedalus non délegué" />

            <p>Cliquez dessus pour lancer la procédure. Vous verrez une popup apparaître.</p>

            <img v-lazy-load data-src="/img/tuto-daedalus/delegate-popup.webp" alt="Popup de délégation" />

            <p>Dans la première étape, sélectionnez votre portefeuille fraîchement crée.</p>

            <p>
                Dans la seconde étape, nous allons choisir une Stake Pool. De celle-ci dépendra vos intérêts. Je vous recommande fortement la lecture de notre tutoriel:
                <a href="/tutoriel/comment-choisir-une-stake-pool-pour-deleguer-ses-ADAs">Comment choisir une stake pool pour déleguer ses ADAs</a>. Si vous souhaitez
                supporter la mission de votre serviteur, vous pouvez y chercher le ticker <b>SPFR</b>. Nous en serions ravis.
                Notez qui <b>si vous misez sur ADAs à long-terme</b>, et comme nous, vous misez sur du x50 ou x100, <b>la meilleure stratégie n'est
                    pas nécessairement de miser sur la stake pool qui vous promet le plus d'intérêts</b>, mais <b>de miser sur les stake pools qui favorisent
                    et supportent le mieux le développement de Cardano</b>. Plus d'infos dans l'article.
            </p>

            <img v-lazy-load data-src="/img/tuto-daedalus/delegate-choose-stakepool.webp" alt="Choisir une stake pool" />


            <p>Cliquez sur la pool de votre choisir, puis cliquez sur "Delegate to this pool". Vous passerez à l'étape de validation finale.</p>

            <img v-lazy-load data-src="/img/tuto-daedalus/delegate-finalize.webp" alt="Finaliser la délégation à la stake pool" />

            <p>
                Vous verrez des frais de transaction s'afficher, ici <b>0.17</b> ADAs. Notez que ce n'est pas tout, puis-ce qu'il y aura <b>2 ADAs</b>
                de dépôt de clés. Vous les récupérez lorsque vous dé-déleguez.
                Enfin, finalisez la transaction. Puis attendez 1 à 2 minutes que la transaction soit enregistrée dans la blockchain et synchronisée par
                votre Daedalus. Lorsque ça sera le cas, vous verrez votre solde changer:
            </p>

            <img v-lazy-load data-src="/img/tuto-daedalus/after-delegate.webp" alt="Délégation enregistrée dans la blockchain Cardano" />

            <p>
                Votre délégation est maintenant enregistrée dans la blockchain. Vous pouvez fermer Daedalus Wallet, pas besoin de le garder ouvert.
                Si vous l'avez installé sur un support externe, débranchez votre disque dur / clé usb / secure drive et rangez le dans un endroit sécurisé.
            </p>

            <h2>Consulter votre compte de staking et vos récompenses</h2>
            <p>
                Pour suivre votre compte en temps réel, cliquez sur le deuxième onglet du menu de gauche.
            </p>

            <p>
                Dans l'onglet <b>Délegation Center</b> du menu vertical, vous trouverez les options pour suivre votre délégation en cours, et changer de stake pool si vous le souhaitez. Tout changement
                sera effectif à la fin de l'époque en cours (5 jours).
            </p>

            <p>
                Dans l'onglet <b>Rewards</b>, vous y trouverez vos récompenses de staking. Elles sont payées tous les 5 jours (à la fin de l'époque en
                cours). Notez que la 1e époque durant laquelle vous déleguez ne compte pas (incomplète).
            </p>

            <img v-lazy-load data-src="/img/tuto-daedalus/stakepool-rewards.webp" alt="Récompenses de délégation à une stake pool" />

            <p> Sur le portefeuille de l'exemple, mes récompenses sont toujours à 0, mais sur le précédent, j'ai touché 49 ADAs jusqu'à présent.</p>

            <p> Bonne délégation à tous !</p>
    </div>
</template>

<script>
import createHeader from "../../js/meta.js";

export default {
    layout: "DocLayout",
    head: createHeader({
        title:
            "Comment déléguer ses ADAs (contre des intérêts) avec Daedalus - Stakepool.fr",
        description:
            "Apprenez à déleguer vos ADAs pour participer au fonctionnement de la blockchain Cardano, contre des interêts. Tutoriel avec le portefeuille Daedalus."
    })
};
</script>